import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchSessions, revokeSession, revokeOtherSessions } from '../../redux/thunk/sessionsThunk';
import { logoutUser } from '../../redux/thunk/authThunk';

/**
 * Account → Devices: every place this account is signed in, with "Sign out"
 * per device and "Sign out other devices". Backed by the auth service's
 * session list; a signed-out device loses access at its next token refresh
 * (within fifteen minutes), which the copy says.
 */

const CLIENT_LABEL = { desktop: 'Desktop app', web: 'Web', ios: 'iPhone / iPad', android: 'Android' };

function relative(ts) {
    if (!ts) return 'unknown';
    const diff = Date.now() - new Date(ts).getTime();
    if (!Number.isFinite(diff)) return 'unknown';
    const m = Math.round(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h ago`;
    const d = Math.round(h / 24);
    return d === 1 ? 'yesterday' : `${d} days ago`;
}

const sectionLabel = {
    fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    color: 'var(--color-text-tertiary)', marginBottom: 8,
};
const rowButton = {
    background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 8,
    color: 'var(--color-text-primary)', fontSize: 12, fontWeight: 600, padding: '4px 10px', cursor: 'pointer',
};

function DevicesSection() {
    const dispatch = useDispatch();
    const [state, setState] = useState({ loading: true, error: null, sessions: [], currentSessionId: null });
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const res = await dispatch(fetchSessions()).unwrap();
            setState({ loading: false, error: null, sessions: res.sessions, currentSessionId: res.currentSessionId });
        } catch (err) {
            setState((s) => ({ ...s, loading: false, error: err?.message || 'Could not load devices' }));
        }
    }, [dispatch]);

    useEffect(() => { load(); }, [load]);

    const signOut = async (session) => {
        const current = session.current || (state.currentSessionId && session.id === state.currentSessionId);
        if (current) {
            dispatch(logoutUser());
            return;
        }
        setBusy(session.id);
        try {
            await dispatch(revokeSession({ sessionId: session.id, current: false })).unwrap();
            await load();
        } catch (err) {
            setState((s) => ({ ...s, error: err?.message || 'Could not sign that device out' }));
        } finally {
            setBusy(null);
        }
    };

    const signOutOthers = async () => {
        setBusy('others');
        try {
            await dispatch(revokeOtherSessions()).unwrap();
            await load();
        } catch (err) {
            setState((s) => ({ ...s, error: err?.message || 'Could not sign other devices out' }));
        } finally {
            setBusy(null);
        }
    };

    const others = state.sessions.filter((s) => !(s.current || (state.currentSessionId && s.id === state.currentSessionId)));

    return (
        <div style={{ marginTop: 28 }} data-testid="devices-section">
            <div style={{ ...sectionLabel, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Devices</span>
                {others.length > 0 && state.currentSessionId && (
                    <button type="button" style={rowButton} onClick={signOutOthers} disabled={busy !== null}>
                        {busy === 'others' ? 'Signing out…' : `Sign out other devices (${others.length})`}
                    </button>
                )}
            </div>
            <div className="account-profile-card" style={{ borderRadius: 14, overflow: 'hidden' }}>
                {state.loading && <div style={{ padding: 14, fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading devices…</div>}
                {!state.loading && state.error && (
                    <div style={{ padding: 14, fontSize: 13, color: 'var(--color-danger)' }} role="alert">
                        {state.error}{' '}
                        <button type="button" className="auth-link" onClick={load} style={{ marginLeft: 6 }}>Retry</button>
                    </div>
                )}
                {!state.loading && !state.error && state.sessions.length === 0 && (
                    <div style={{ padding: 14, fontSize: 13, color: 'var(--color-text-secondary)' }}>No signed-in devices.</div>
                )}
                {!state.loading && !state.error && state.sessions.map((s, i) => {
                    const current = s.current || (state.currentSessionId && s.id === state.currentSessionId);
                    return (
                        <div key={s.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                            borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                                <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {s.deviceName || CLIENT_LABEL[s.client] || 'Unknown device'}
                                    {current && (
                                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: 'var(--color-accent)', border: '1px solid var(--color-accent)', borderRadius: 8, padding: '0 6px' }}>
                                            This device
                                        </span>
                                    )}
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                                    {CLIENT_LABEL[s.client] || 'App'} · last active {relative(s.lastUsedAt)} · signed in {relative(s.createdAt)}
                                </span>
                            </div>
                            <button type="button" style={rowButton} onClick={() => signOut(s)} disabled={busy !== null}>
                                {busy === s.id ? 'Signing out…' : 'Sign out'}
                            </button>
                        </div>
                    );
                })}
            </div>
            <p style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
                A signed-out device loses access within 15 minutes. If you see a device you don't recognise, sign it out and change your password from Forgot Password.
            </p>
        </div>
    );
}

export default DevicesSection;
